import React from 'react';

/**
 * Black & white A5/C5 (half-A4) pathology slip — printer-friendly.
 * Larger, clearly-readable text. Footer pinned to the bottom of the page.
 */
export const PathologyPrintSlip = ({
  className,
  billInfo,
  table,
  summary,
  note,
  footer,
  isPaid,
  isFree = false,
  amountInWords,
  title = 'Pathology Invoice',
}) => {
  const printedAt = new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
  return (
    <div className={className} style={{ display: 'none' }}>
      <style>
        {`
          @media print {
            @page {
              size: 162mm 229mm;   /* C5 */
              margin: 8mm;
            }
            html, body { margin: 0 !important; padding: 0 !important; }
            body * { visibility: hidden; }
            .print-wrapper, .print-wrapper * { visibility: hidden; }
            .${className}, .${className} * { visibility: visible; }
            .${className} {
              display: block !important;
              position: absolute; left: 0; top: 0; width:100%;
            }

            .pslip {
              display: flex; flex-direction: column;
              min-height: 229mm; box-sizing: border-box;
              padding: 6mm 7mm 4mm;
              font-size: 13pt; line-height: 1.3;
            }
            .pslip * { color: #000 !important; }

            /* reserve space so content never sits under the fixed footer */
            .pslip-body { flex: 1 0 auto; }

            /* ---- Header (text only, no logo) ---- */
            .pslip-header {
              text-align: center;
              padding-bottom: 7px;
              border-bottom: 2px solid #000;
              margin-bottom: 10px;
            }
            .pslip-name { font-size: 25pt; font-weight: 800; letter-spacing: 0.5px; line-height: 1.1; }
            .pslip-addr { font-size: 11.5pt; margin-top: 3px; }

            .pslip-title {
              text-align: center;
              font-size: 11pt;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              margin: 7px 0 11px;
            }

            /* ---- Bill info (two columns) ---- */
            .pslip-info {
              display: grid; grid-template-columns: 1fr 1fr;
              gap: 1px; align-items: start;
              font-size: 11.5pt; line-height: 1.4; margin-bottom: 7px;
            }
            .pslip-info > div { min-width: 0; }
            .pslip-info p { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .pslip-info p.full {
              grid-column: 1 / -1;
              white-space: normal; overflow: visible;
            }
            .pslip-info strong { font-weight: 700; }

            /* ---- Test table ---- */
            .pslip table { width: 100%; border-collapse: collapse; margin-bottom: 7px; font-size: 11pt; }
            .pslip thead { display: table-header-group; }
            .pslip tr { page-break-inside: avoid; }
            .pslip th, .pslip td {
              border: 1px solid #000; padding: 3px 6px;
              text-align: left; line-height: 1.2;
            }
            .pslip th { font-weight: 700; font-size: 11pt; }
            .pslip td:last-child, .pslip th:last-child { text-align: right; }

            /* ---- Summary (stamp left, totals right) ---- */
            .pslip-summary { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-top: 2px; }
            .pslip-summary-inner { width: 55%; }
            .pslip-summary-inner > div > div {
              display: flex !important;
              justify-content: space-between !important;
              padding: 1.5px 6px; 
              border-bottom: 1px dashed #999; 
              font-size: 11pt;
            }
            .pslip-summary-inner > div > div:last-child {
              font-weight: 800;
              font-size: 12.5pt;
              border-top: 2px solid #000;
              border-bottom: none;
              margin-top: 2px;
              padding-top: 3px;
            }

            /* ---- PAID / DUE stamp ---- */
            .pslip-stamp {
              display: inline-block;
              border: 3px solid #000;
              border-radius: 5px;
              padding: 5px 20px;
              font-size: 26pt;
              font-weight: 900;
              letter-spacing: 2px;
              transform: rotate(-12deg);
              opacity: 0.55;
            }

            /* ---- Amount in words ---- */
            .pslip-inwords {
              margin-top: 5px;
              font-size: 11pt;
              font-style: italic;
              border-top: 1px dotted #999;
              padding-top: 6px;
            }

            /* ---- Note ---- */
            .pslip .note-box { 
               border-left: 2px solid #000; 
               padding: 5px; 
               font-size: 11pt; 
               margin-top: 6px; 
            }

            .pslip-footer { padding-top: 2px; }

            .pslip-notice {
              text-align: center;
              font-size: 11pt;
              font-weight: 700;
              margin-top: 8px;
              padding-top: 6px;
              border-top: 1px dashed #000;
            }
            .pslip-notice2 {
              text-align: center;
              font-size: 11pt;
              font-weight: 600;
              margin-top: 2px;
            }
            .pslip-powered {
              text-align: center;
              font-size: 9pt;
              margin-top: 6px;
              padding-top: 4px;
              border-top: 1px solid #000;
            }

            /* faint watermark, repeats on every page */
            .pslip::before {
              content: "Mukti Hospital";
              position: fixed;
              top: 42%; left: 50%;
              transform: translate(-50%, -50%) rotate(-30deg);
              font-size: 56pt;
              font-weight: 800;
              color: rgba(0,0,0,0.05);
              z-index: -1;
              white-space: nowrap;
            }
          }
        `}
      </style>

      <div className="pslip">
        {/* ===== Flowing content ===== */}
        <div className="pslip-body">
          <div className="pslip-header">
            <div className="pslip-name">Mukti Hospital</div>
            <div className="pslip-addr">Race Course, Sasongacha, Cumilla</div>
            <div className="pslip-addr">Pathology: 01673-625038 &nbsp;·&nbsp; Emergency: 01711-796905</div>
          </div>

          <div className="pslip-title">{title}</div>

          {billInfo && <div className="pslip-info">{billInfo}</div>}

          {table}

          {summary && (
            <div className="pslip-summary">
              <div className="pslip-stamp-wrap">
                {isFree ? (
                  <span className="pslip-stamp">FREE</span>
                ) : isPaid !== undefined ? (
                  <span className="pslip-stamp">{isPaid ? 'PAID' : 'DUE'}</span>
                ) : null}
              </div>
              <div className="pslip-summary-inner">{summary}</div>
            </div>
          )}

          {amountInWords && (
            <div className="pslip-inwords"><strong>In Words:</strong> {amountInWords}</div>
          )}

          {note && <div>{note}</div>}
        </div>

        {/* ===== Bottom block, pinned to page bottom ===== */}
        <div className="pslip-bottom">
          {footer && <div className="pslip-footer">{footer}</div>}

          <div className="pslip-notice">
            রিপোর্ট বিল করার ৩০ দিনের মধ্যে না নিলে রিপোর্ট পাওয়া যাবে না।
          </div>
          <div className="pslip-notice2">
            বিঃদ্রঃ দয়া করে রক্ত প্রদানের ৩ ঘন্টা পর রিপোর্টের জন্য কাউন্টারে যোগাযোগ করুন।
          </div>
          <div className="pslip-powered">
            Printed: {printedAt} &nbsp;|&nbsp; Software Powered By: Microters · microters.com
          </div>
        </div>
      </div>
    </div>
  );
};